/**
 * useAuditEvents -- paginated query against P4.3's
 * GET /v1/admin/audit/events endpoint (P39.1).
 *
 * Admin-only (the endpoint returns 403 for non-admin). Callers
 * gate the surrounding UI with useMe() so the unauthenticated
 * call doesn't fire at all.
 */
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type { ApiError } from "shared/api/client";
import type { AuditEventListResponse, AuditEventQuery } from "shared/api/types";

function buildQueryString(query: AuditEventQuery): string {
  const params = new URLSearchParams();
  if (query.event_type_prefix) {
    params.set("event_type_prefix", query.event_type_prefix);
  }
  if (query.event_type) {
    params.set("event_type", query.event_type);
  }
  if (query.entity_type) {
    params.set("entity_type", query.entity_type);
  }
  if (query.entity_id) {
    params.set("entity_id", query.entity_id);
  }
  if (query.actor_user_id) {
    params.set("actor_user_id", query.actor_user_id);
  }
  if (query.since) {
    params.set("since", query.since);
  }
  if (query.until) {
    params.set("until", query.until);
  }
  if (query.limit !== undefined) {
    params.set("limit", String(query.limit));
  }
  if (query.offset !== undefined) {
    params.set("offset", String(query.offset));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useAuditEvents(
  query: AuditEventQuery,
  options: { enabled?: boolean } = {},
): UseQueryResult<AuditEventListResponse> {
  return useQuery<AuditEventListResponse>({
    queryKey: ["admin-audit-events", query] as const,
    queryFn: () =>
      api.get<AuditEventListResponse>(
        `/v1/admin/audit/events${buildQueryString(query)}`,
      ),
    enabled: options.enabled ?? true,
  });
}

export interface AuditEventType {
  event_type: string;
  count: number;
}

/**
 * H12 -- the event types the log actually holds, for the prefix
 * filter's suggestions.
 *
 * Derived server-side from the rows, so it cannot offer a type that
 * is not there or omit one that is. Cached for the session: the
 * vocabulary changes only when a new kind of event first fires.
 */
export function useAuditEventTypes(): UseQueryResult<AuditEventType[], ApiError> {
  return useQuery<AuditEventType[], ApiError>({
    queryKey: ["admin", "audit", "event-types"],
    queryFn: () => api.get<AuditEventType[]>("/v1/admin/audit/event-types"),
    staleTime: 5 * 60_000,
  });
}
